import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SwPush } from '@angular/service-worker';
import { NotificationHelperService } from '../../../helpers/notification-helper.service';
import { AuthService } from '../../../api/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-checkbox-type-seven',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './checkbox-type-seven.component.html',
  styleUrl: './checkbox-type-seven.component.css',
})
export class CheckboxTypeSevenComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() visibleFields!: { [key: string]: boolean };
  @Input() requiredMap: { [key: string]: boolean } = {};
  @Output() visibilityChanged = new EventEmitter<{
    visibilityMap: { [key: string]: boolean };
    toggleField: string;
    skipTaskMappingDependency?: boolean;
  }>();
  selectedIds: string[] = [];
  showValidationError: boolean = false;
  requiredFields: any;
  isNotificationEnabled = false;

  readonly VAPID_PUBLIC_KEY =
    'BCQ9SAxhT-yxNgs1Ae8tfQMgLMKzEAYX_Hnryus872E_LcjS5dv0z6_fUL34lFRYRpiA2t2aLZkFwoyeTFutyIg';
  profileResponse: any;
  constructor(
    private swPush: SwPush,
    private http: HttpClient,
    private notificationHelper: NotificationHelperService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private toaster: ToastrService,
  ) {}
  ngOnInit() {
    const token = localStorage.getItem('accessToken');
    this.getProfileDetails(token);
    // Default OFF
    const initialValue = this.fields?.value === true;

    this.control?.setValue(initialValue, {
      emitEvent: false,
    });

    this.isNotificationEnabled = initialValue;

    // Restore tags only if control is actually enabled
    if (this.control?.value === true) {
      this.restoreTagsFromSelectedOptions();
    }

    // Check existing browser subscription
    if (this.swPush.isEnabled) {
      this.swPush.subscription.subscribe((sub) => {
        const enabled = !!sub;
        this.isNotificationEnabled = enabled;
        // Sync switch with actual subscription state
        this.control?.setValue(enabled, { emitEvent: false });
        this.cdr.detectChanges();
      });

      // Foreground messages
      this.swPush.messages.subscribe((message) => {
        this.cdr.detectChanges();
      });

      // Notification click
      this.swPush.notificationClicks.subscribe(({ action, notification }) => {
        window.open(notification.data?.url || '/', '_blank');
        this.cdr.detectChanges();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['formGroup'] || changes['fields']) &&
      this.formGroup &&
      this.fields
    ) {
      this.initializeControl();
    }
  }

  getProfileDetails(token: any) {
    this.authService.getProfile(token).subscribe({
      next: (data) => {
        this.profileResponse = data.data;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error', err);
        this.cdr.detectChanges();
      },
    });
  }

  initializeControl() {
    if (!this.fields) return;

    const enabled = this.fields.value === true;

    this.control?.setValue(enabled, { emitEvent: false });
    this.isNotificationEnabled = enabled;

    this.restoreTagsFromSelectedOptions();
  }

  get control() {
    return this.formGroup.get(this.fields.fieldName);
  }

  showNotification(): void {
    const notification = new Notification('Notifications Enabled', {
      body: 'You will now receive browser notifications.',
      icon: './assets/media/icons/mini-logo.png',
    });

    setTimeout(() => {
      notification.close();
    }, 5000);
  }

  restoreTagsFromSelectedOptions(): void {
    const tagsControl = this.formGroup.get(this.fields.fieldName + 'Tags');

    if (!this.control || !tagsControl) {
      return;
    }

    const checked =
      this.control.value === true && this.isNotificationEnabled === true;

    tagsControl.setValue(checked ? [true] : [], {
      emitEvent: false,
    });
  }

  private getQuestionLabel(): string {
    return (
      this.fields?.displayName ||
      this.fields?.label ||
      this.fields?.fieldName ||
      ''
    );
  }

  private resolveFieldName(field: any): string {
    if (!field) return '';

    // ✅ Case 1: direct string
    if (typeof field.fieldName === 'string') {
      return field.fieldName;
    }

    // ✅ Case 2: object { label, value }
    if (field.fieldName?.label) {
      return field.fieldName.label;
    }

    // ✅ Case 3: value[] format
    if (Array.isArray(field)) {
      return this.getValueByKey(field, 'fieldName') || '';
    }

    // ✅ Case 4: field.value is array (very common in your setup)
    if (Array.isArray(field.value)) {
      return this.getValueByKey(field.value, 'fieldName') || '';
    }

    return '';
  }

  private getValueByKey(arr: any[], key: string): any {
    return (arr || []).find((item: any) => item.Key === key)?.Value;
  }

  async onToggleNotifications(event: any) {
    const enabled = event.target.checked;

    if (enabled) {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        // Show browser alert to allow or block the notification (cross-browser)
        const permission = await this.notificationHelper.requestPermission();

        if (permission === 'granted') {
          this.isNotificationEnabled = true;
          this.control?.setValue(true);
          this.restoreTagsFromSelectedOptions();

          const userID = this.profileResponse?.email;
          const success =
            await this.notificationHelper.enableNotifications(userID);

          if (success) {
            if ('serviceWorker' in navigator) {
              try {
                const registration =
                  await navigator.serviceWorker.getRegistration();

                if (registration) {
                  setTimeout(async () => {
                    await registration.showNotification(
                      'eCISO Notifications Enabled',
                      {
                        body: 'You will now receive browser notifications.',
                        icon: './assets/media/icons/mini-logo.png',
                        badge: './assets/media/icons/mini-logo.png',
                        requireInteraction: true,
                      },
                    );
                  }, 1000);
                } else {
                  this.showNotification();
                }
              } catch (err) {
                this.showNotification();
              }
            } else {
              this.showNotification();
            }
          } else {
            // revert toggle if subscription failed
            this.toaster.error(
              'Failed to complete push subscription. Please check network/VAPID configuration.',
              'Error',
            );
            this.isNotificationEnabled = false;
            this.control?.setValue(false);
            this.restoreTagsFromSelectedOptions();
            event.target.checked = false;
          }
        } else {
          // If permission is blocked or dismissed
          this.toaster.error(
            permission === 'denied'
              ? 'Notifications are blocked for this site. Allow them in your browser site settings and try again.'
              : 'Notification permission was not granted.',
            'Error',
          );
          this.isNotificationEnabled = false;
          this.control?.setValue(false);
          this.restoreTagsFromSelectedOptions();
          event.target.checked = false;
        }
      }
    } else {
      // Synchronously set to false to prevent UI reverting while waiting for async unsubscribe
      this.isNotificationEnabled = false;
      this.control?.setValue(false);
      this.restoreTagsFromSelectedOptions();
      event.target.checked = false;

      // Perform unsubscribe asynchronously in the background
      this.disableNotifications().catch((err) =>
        console.error('Error disabling notifications:', err),
      );
    }
  }

  async enableNotifications() {
    try {
      if (!this.swPush.isEnabled) {
        return;
      }

      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: this.VAPID_PUBLIC_KEY,
      });
      this.isNotificationEnabled = true;
      this.control?.setValue(true);
      new Notification('eCISO Notifications Enabled', {
        body: 'You will now receive browser notifications.',
        icon: './assets/media/icons/mini-logo.png',
      });
    } catch (err) {
      this.control?.setValue(false);
    }
  }

  async disableNotifications() {
    try {
      if (this.swPush.isEnabled) {
        const subscription = await firstValueFrom(this.swPush.subscription);

        if (subscription) {
          await subscription.unsubscribe();
        }
      } else if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager?.getSubscription();
        if (sub) {
          await sub.unsubscribe();
        }
      }

      this.isNotificationEnabled = false;
      this.control?.setValue(false);
      this.restoreTagsFromSelectedOptions();
    } catch (err) {
      console.error(err);
    }
  }

  executeFieldDependencies(trigger: string) {
    const dependencies = this.fields?.fieldDependencies || [];

    dependencies.forEach((dependency: any) => {
      if (dependency.trigger !== trigger) {
        return;
      }

      dependency.condition.forEach((conditionGroup: any) => {
        const matched = conditionGroup.conditions.every((condition: any) => {
          if (condition.type === 'field') {
            return condition.equals.includes(
              this.formGroup.get(condition.value)?.value,
            );
          }

          return false;
        });

        if (matched) {
          conditionGroup.actions.forEach((action: any) => {
            if (action.actionType === 'api') {
              this.http
                .request(action.value.method, action.value.apiUrl, {
                  body: action.value.body,
                  headers: action.value.headers,
                })
                .subscribe();
            }
          });
        }
      });
    });
  }
}
